import {
  addDays,
  isSameDay,
  parseISO,
  startOfWeek,
  differenceInMinutes,
} from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import type { ViewType, DateRange, TimeFormat, HolidayDefinition, ScheduleEvent } from '../types';

// ─── Timezone / time-display helpers ──────────────────────────────────────

/** Returns the DST-aware short abbreviation for an IANA timezone at a given moment. */
export function getTimezoneAbbr(timezone: string, date: Date): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName')?.value ?? timezone
    );
  } catch {
    return timezone;
  }
}

/**
 * Formats an hour (0–23) and minute as a time label string.
 * '12h'       → "8:00 AM" / "12:30 PM"
 * '12h-short' → "8A" / "8:30P"
 * '24h'       → "08:00" / "12:30"
 */
export function formatTimeLabel(hour: number, minute: number, format: TimeFormat): string {
  if (format === '24h') {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }
  const isPm = hour >= 12;
  const h12 = hour % 12 || 12;
  if (format === '12h-short') {
    const minStr = minute === 0 ? '' : `:${String(minute).padStart(2, '0')}`;
    return `${h12}${minStr}${isPm ? 'P' : 'A'}`;
  }
  return `${h12}:${String(minute).padStart(2, '0')} ${isPm ? 'PM' : 'AM'}`;
}

// ─── Time string helpers ───────────────────────────────────────────────────

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/** Convert '08:00' → a Date with that hour/minute (year/day irrelevant). */
export function parseTimeToDate(timeStr: string): Date {
  const [h, m] = timeStr.split(':').map(Number);
  return new Date(2000, 0, 1, h, m, 0, 0);
}

// ─── Timezone helpers ──────────────────────────────────────────────────────

export function toLocal(utcString: string, tz: string): Date {
  return toZonedTime(parseISO(utcString), tz);
}

export function toUtcString(localDate: Date, tz: string): string {
  return fromZonedTime(localDate, tz).toISOString();
}

export function formatLocal(date: Date, tz: string, fmt: string): string {
  return formatInTimeZone(date, tz, fmt);
}

export function nowInTimezone(tz: string): Date {
  return toZonedTime(new Date(), tz);
}

export function isSameDayLocal(utcString: string, dayDate: Date, tz: string): boolean {
  return isSameDay(toLocal(utcString, tz), dayDate);
}

export function durationMinutes(startUtc: string, endUtc: string): number {
  return differenceInMinutes(parseISO(endUtc), parseISO(startUtc));
}

// ─── View range for data requests ─────────────────────────────────────────

export function getViewRange(view: ViewType, currentDate: Date, tz: string, agendaDays = 30): DateRange {
  let start: Date;
  let end: Date;

  if (view === 'day') {
    start = currentDate;
    end = currentDate;
  } else if (view === 'weekdays' || view === 'fullWeek') {
    const monday = startOfWeek(currentDate, { weekStartsOn: 1 });
    start = monday;
    end = addDays(monday, view === 'fullWeek' ? 6 : 4);
  } else if (view === 'agenda') {
    start = currentDate;
    end = addDays(currentDate, agendaDays - 1);
  } else {
    start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  }

  return {
    startUtc: fromZonedTime(new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0), tz).toISOString(),
    endUtc: fromZonedTime(new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59), tz).toISOString(),
  };
}

// ─── Navigation ────────────────────────────────────────────────────────────

export function navigate(view: ViewType, currentDate: Date, direction: 'prev' | 'next', agendaDays = 30): Date {
  const delta = direction === 'next' ? 1 : -1;
  if (view === 'day') return addDays(currentDate, delta);
  if (view === 'weekdays' || view === 'fullWeek') return addDays(currentDate, delta * 7);
  if (view === 'agenda') return addDays(currentDate, delta * agendaDays);
  const d = new Date(currentDate);
  d.setMonth(d.getMonth() + delta);
  return d;
}

// ─── Misc ──────────────────────────────────────────────────────────────────

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export const PIXELS_PER_MINUTE = 1.5;

// ─── Holiday expansion ─────────────────────────────────────────────────────

/**
 * Converts HolidayDefinitions into synthetic read-only all-day ScheduleEvents
 * for the years covered by viewRange. Wall-clock midnight in `timezone` is used
 * so the event always lands on the correct local calendar date.
 */
export function expandHolidays(
  holidays: HolidayDefinition[],
  viewRange: DateRange,
  timezone: string,
): ScheduleEvent[] {
  const startYear = new Date(viewRange.startUtc).getUTCFullYear();
  const endYear = new Date(viewRange.endUtc).getUTCFullYear();
  const events: ScheduleEvent[] = [];

  for (const holiday of holidays) {
    const parts = holiday.date.split('-');
    const month = parseInt(parts[1], 10) - 1; // 0-based month
    const day = parseInt(parts[2], 10);

    const years = holiday.recurring
      ? Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i)
      : [parseInt(parts[0], 10)];

    for (const year of years) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      // Wall-clock midnight → UTC so the event appears on the correct local date
      const startUtc = fromZonedTime(new Date(year, month, day, 0, 0, 0), timezone).toISOString();
      const endUtc = fromZonedTime(new Date(year, month, day, 23, 59, 59), timezone).toISOString();

      events.push({
        id: `__holiday__${holiday.name}__${dateKey}`,
        title: holiday.name,
        startUtc,
        endUtc,
        isAllDay: true,
        editable: false,
        deletable: false,
        color: holiday.color ?? '#6366f1',
        backgroundColor: holiday.color ?? '#6366f1',
      });
    }
  }

  return events;
}

// ─── DST resolution ────────────────────────────────────────────────────────

/**
 * When observeDaylightSaving is false, returns a fixed-offset Etc/GMT timezone
 * that matches the standard (non-DST) UTC offset of the given IANA timezone.
 * Falls through unchanged for zones with no DST or fractional-hour offsets
 * (both are already effectively DST-free in practice).
 */
export function resolveTimezone(timezone: string, observeDaylightSaving = true): string {
  if (observeDaylightSaving !== false) return timezone;

  // Sample both solstices. Math.min gives standard time for both hemispheres:
  // northern (UTC-N): Jan offset < Jul offset → min picks Jan (winter = standard) ✓
  // southern (UTC+M): Jul offset < Jan offset → min picks Jul (winter = standard) ✓
  const jan = getTimezoneOffset(timezone, new Date(2024, 0, 15));
  const jul = getTimezoneOffset(timezone, new Date(2024, 6, 15));

  if (jan === jul) return timezone; // No DST — original zone is already fixed

  const standardMs = Math.min(jan, jul);
  const hours = standardMs / (60 * 60 * 1000);

  if (!Number.isInteger(hours)) return timezone; // Fractional offset — no Etc/GMT equivalent

  // Etc/GMT sign is POSIX-reversed: UTC-6 → Etc/GMT+6, UTC+10 → Etc/GMT-10
  const etcHours = -hours;
  if (etcHours === 0) return 'UTC';
  return etcHours > 0 ? `Etc/GMT+${etcHours}` : `Etc/GMT${etcHours}`;
}

/**
 * Converts a UTC ISO string to the "YYYY-MM-DDTHH:MM" value format required by
 * `<input type="datetime-local">`, expressed in the scheduler's configured timezone
 * (with DST handling matching `observeDaylightSaving`).
 *
 * Uses `formatInTimeZone` rather than `toZonedTime(...).getHours()` so the result
 * is never influenced by the browser's local timezone.
 */
export function utcToScheduleLocal(utcIso: string, timezone: string, observeDaylightSaving = true): string {
  if (!utcIso) return '';
  const tz = resolveTimezone(timezone, observeDaylightSaving);
  return formatInTimeZone(new Date(utcIso), tz, "yyyy-MM-dd'T'HH:mm");
}

/**
 * Converts a "YYYY-MM-DDTHH:MM" value from `<input type="datetime-local">` back to a
 * UTC ISO string, interpreting the local time in the scheduler's configured timezone
 * (with DST handling matching `observeDaylightSaving`).
 *
 * Passes the string directly to `fromZonedTime` (not `new Date(str)`) so that
 * date-fns-tz's string path is used, which interprets the value in the target
 * timezone rather than in the browser's local timezone.
 */
export function scheduleLocalToUtc(localStr: string, timezone: string, observeDaylightSaving = true): string {
  if (!localStr) return '';
  const tz = resolveTimezone(timezone, observeDaylightSaving);
  return fromZonedTime(localStr, tz).toISOString();
}
